import { CSSProperties, useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WORDMARK_LETTERS,
  WORDMARK_VIEWBOX,
} from 'components/BrandLogo/Wordmark';
import styles from './Loader.module.css';

interface Props {
  spinnerColor?: 'white' | 'default';
  /**
   * On phones the loader is pinned to the middle of the screen. Pass `inline`
   * for a loader that fills in one section of a page whose other content is
   * already showing, so it stays in that section instead of floating over it.
   */
  inline?: boolean;
}

/**
 * Page loader: the TOTTALO logotype lights up letter by letter, a glint runs
 * across the finished word, then it fades out in the same wave and repeats.
 * The letters are the header logotype's own glyphs; the colours come from the
 * palette's banner ramp, so it is gold on Golden, green on Green, blue on
 * tottalo.
 */
export const Loader = ({ spinnerColor = 'default', inline = false }: Props) => {
  const { t } = useTranslation();
  const uid = useId().replace(/:/g, '');
  const rampId = `loader-ramp-${uid}`;
  const glintId = `loader-glint-${uid}`;
  const clipId = `loader-clip-${uid}`;

  return (
    <div
      className={styles.wrapper}
      role="status"
      aria-label={t('loading_label')}
      data-spinner-color={spinnerColor}
      data-placement={inline ? 'inline' : 'screen'}
    >
      <svg
        className={styles.mark}
        viewBox={WORDMARK_VIEWBOX}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={rampId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className={styles.ramp1} />
            <stop offset="52%" className={styles.ramp2} />
            <stop offset="100%" className={styles.ramp3} />
          </linearGradient>
          <linearGradient id={glintId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" className={styles.glintEdge} />
            <stop offset="50%" className={styles.glintCore} />
            <stop offset="100%" className={styles.glintEdge} />
          </linearGradient>
          <clipPath id={clipId}>
            {WORDMARK_LETTERS.map((d) => (
              <path key={d} d={d} clipRule="evenodd" />
            ))}
          </clipPath>
        </defs>
        {WORDMARK_LETTERS.map((d, i) => (
          <g key={d} style={{ '--i': i } as CSSProperties}>
            <path className={styles.ghost} d={d} fillRule="evenodd" />
            <path
              className={styles.lit}
              d={d}
              fillRule="evenodd"
              fill={`url(#${rampId})`}
            />
          </g>
        ))}
        <g clipPath={`url(#${clipId})`}>
          <rect
            className={styles.glint}
            x="-170"
            y="-20"
            width="110"
            height="140"
            fill={`url(#${glintId})`}
          />
        </g>
      </svg>
      <span className={styles.baseline} aria-hidden="true" />
    </div>
  );
};
